import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const retryAxiosCall = async (axiosCall, maxRetries = 2, delay = 500) => {
  let lastError;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await axiosCall();
    } catch (error) {
      lastError = error;
      if (error.response && [401, 403, 422].includes(error.response.status)) {
        throw error;
      }
      if (attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  throw lastError;
};

export const createListing = async (formData) => {
  try {
    const response = await axios.post(`${API_URL}/listings`, formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 401) {
        throw new Error('Session expired. Please login again');
      }
      throw new Error(error.response.data.message || 'Server error');
    } else if (error.request) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

export const getListings = async (filters = {}, signal) => {
  try {
    const response = await axios.get(`${API_URL}/listings`, {
      params: filters,
      timeout: 30000,
      signal,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getUserListings = async () => {
  try {
    const response = await retryAxiosCall(() =>
      axios.get(`${API_URL}/listings/user-listings`, {
        withCredentials: true,
        timeout: 10000
      })
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateListing = async (id, formData) => {
  try {
    const response = await axios.put(`${API_URL}/listings/${id}`, formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Session expired. Please login again');
    }
    throw error;
  }
};

export const deleteListing = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/listings/${id}`, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const updateListingStatus = async (id, status) => {
  try {
    const response = await axios.put(
      `${API_URL}/listings/${id}/status`,
      { status },
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Authentication error. Please login again.');
    }
    throw new Error(error.response?.data?.message || error.message || 'Error updating status');
  }
};

export const incrementListingViews = async (id) => {
  try {
    const response = await axios.put(`${API_URL}/listings/${id}/views`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const checkUserViewedListing = (listingId) => {
  try {
    const viewedListings = JSON.parse(localStorage.getItem('viewedListings') || '{}');
    const lastViewTime = viewedListings[listingId];
    return lastViewTime ? (Date.now() - lastViewTime < 3600000) : false;
  } catch {
    return false;
  }
};

export const recordListingView = (listingId) => {
  try {
    const viewedListings = JSON.parse(localStorage.getItem('viewedListings') || '{}');
    viewedListings[listingId] = Date.now();
    localStorage.setItem('viewedListings', JSON.stringify(viewedListings));
  } catch {
    // silently fail
  }
};

export const getPendingListings = async () => {
  try {
    const response = await axios.get(`${API_URL}/listings/pending`, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const approveListing = async (listingId) => {
  try {
    const response = await axios.put(
      `${API_URL}/listings/${listingId}/approve`,
      { status: 'active' },
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to approve listing');
  }
};

export const rejectListing = async (listingId) => {
  try {
    const response = await axios.put(
      `${API_URL}/listings/${listingId}/reject`,
      {},
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getListingById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/listings/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

export const getListingsByCategoryAndSubcategory = async (category, subcategory = 'all') => {
  try {
    const response = await axios.get(`${API_URL}/listings/category/${category}/${subcategory || 'all'}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};
